import {
  type SaveWorkforceAccount,
  type WorkforceAccount,
  type WorkforcePreferences,
  WorkforcePreferencesSchema,
} from "@emr/contracts";
import { useEffect, useRef, useState } from "react";

export const landingPages = [
  ["/", "Today"],
  ["/schedule", "Schedule"],
  ["/patients", "Patients"],
  ["/attention", "Attention"],
  ["/billing", "Billing"],
  ["/admin", "Admin"],
] as const;
export function allowedLandingPages(role: string) {
  return landingPages.filter(([path]) =>
    path === "/billing"
      ? ["physician", "billing", "clinic_admin"].includes(role)
      : path === "/admin"
        ? ["clinic_admin", "auditor"].includes(role)
        : true,
  );
}
export function applyWorkforcePreferences(preferences: WorkforcePreferences) {
  document.documentElement.dataset.appearance = preferences.appearance;
  document.documentElement.dataset.density = preferences.density;
  document.documentElement.dataset.reducedMotion = String(preferences.reducedMotion);
}
export function PreferencesSettings({
  profile,
  save,
  onDirtyChange,
  disabled = false,
  reload,
}: {
  profile: WorkforceAccount;
  save(command: SaveWorkforceAccount): Promise<unknown>;
  onDirtyChange?: (dirty: boolean) => void;
  disabled?: boolean;
  reload?: () => Promise<unknown>;
}) {
  const [draft, setDraft] = useState<{ preferences: WorkforcePreferences; version: number } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const preferences = draft?.preferences ?? profile.preferences;
  useEffect(() => {
    onDirtyChange?.(draft !== null || busy);
    return () => onDirtyChange?.(false);
  }, [draft, busy, onDirtyChange]);
  const edit = (patch: Partial<WorkforcePreferences>) => {
    setDraft({
      preferences: { ...preferences, ...patch },
      version: draft?.version ?? profile.profileVersion,
    });
    setMessage("");
  };
  return (
    <section className="account-section" id="preferences">
      <h2>Preferences</h2>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!draft || busy) return;
          setBusy(true);
          setError("");
          const parsed = WorkforcePreferencesSchema.parse(draft.preferences);
          const body = JSON.stringify(draft);
          if (attempt.current?.body !== body) attempt.current = { body, key: crypto.randomUUID() };
          void save({
            expectedVersion: draft.version,
            idempotencyKey: attempt.current.key,
            changes: { preferences: parsed },
          })
            .then(() => {
              try {
                const key = `emr.sidebar.${profile.tenantId}.${profile.userId}`;
                if (!parsed.rememberSidebar) localStorage.removeItem(key);
                else if (parsed.sidebarCollapsed !== profile.preferences.sidebarCollapsed)
                  localStorage.setItem(key, parsed.sidebarCollapsed ? "1" : "0");
              } catch {
                /* Browser storage is optional. */
              }
              applyWorkforcePreferences(parsed);
              setDraft(null);
              attempt.current = null;
              setMessage("Preferences saved.");
            })
            .catch((cause) =>
              setError(cause instanceof Error ? cause.message : "Preferences could not be saved."),
            )
            .finally(() => setBusy(false));
        }}
      >
        <fieldset disabled={busy || disabled}>
          {disabled ? <p>Save or discard your profile edits before changing preferences.</p> : null}
          <div className="account-field-grid">
            <label>
              Default landing page
              <select
                aria-label="Default landing page"
                value={preferences.landingPage}
                onChange={(event) =>
                  edit({
                    landingPage: WorkforcePreferencesSchema.shape.landingPage.parse(
                      event.target.value,
                    ),
                  })
                }
              >
                {allowedLandingPages(profile.role).map(([path, label]) => (
                  <option key={path} value={path}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Appearance
              <select
                aria-label="Appearance"
                value={preferences.appearance}
                onChange={(event) =>
                  edit({ appearance: event.target.value === "dark" ? "dark" : "light" })
                }
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </label>
            <label>
              Interface density
              <select
                aria-label="Interface density"
                value={preferences.density}
                onChange={(event) =>
                  edit({ density: event.target.value === "compact" ? "compact" : "comfortable" })
                }
              >
                <option value="comfortable">Comfortable</option>
                <option value="compact">Compact</option>
              </select>
            </label>
            <label>
              Sidebar at sign-in
              <select
                aria-label="Sidebar at sign-in"
                value={preferences.sidebarCollapsed ? "collapsed" : "expanded"}
                onChange={(event) => edit({ sidebarCollapsed: event.target.value === "collapsed" })}
              >
                <option value="expanded">Expanded</option>
                <option value="collapsed">Collapsed</option>
              </select>
            </label>
          </div>
          <label className="account-checkbox">
            <input
              type="checkbox"
              checked={preferences.rememberSidebar}
              onChange={(event) => edit({ rememberSidebar: event.target.checked })}
            />
            Remember sidebar changes
          </label>
          <label className="account-checkbox">
            <input
              type="checkbox"
              checked={preferences.reducedMotion}
              onChange={(event) => edit({ reducedMotion: event.target.checked })}
            />
            Reduce motion
          </label>
        </fieldset>
        <div className="account-save-row">
          <button className="primary-button" type="submit" disabled={busy || !draft}>
            {busy ? "Saving…" : "Save preferences"}
          </button>
          {draft ? (
            <button
              className="secondary-button"
              type="button"
              disabled={busy}
              onClick={() => {
                setDraft(null);
                setError("");
              }}
            >
              Discard changes
            </button>
          ) : null}
        </div>
        {error ? (
          <div className="account-error" role="alert">
            <p>{error}</p>
            {reload ? (
              <button
                type="button"
                className="secondary-button"
                disabled={busy}
                onClick={() => {
                  if (
                    draft &&
                    !window.confirm(
                      "Reload preferences and discard your unsaved preference changes?",
                    )
                  )
                    return;
                  setBusy(true);
                  void reload()
                    .then(() => {
                      setDraft(null);
                      setError("");
                      attempt.current = null;
                    })
                    .catch(() => setError("Preferences could not be reloaded."))
                    .finally(() => setBusy(false));
                }}
              >
                Reload preferences
              </button>
            ) : null}
          </div>
        ) : null}
        {message ? <p role="status">{message}</p> : null}
      </form>
    </section>
  );
}
